export async function handleReduceDataModel(currentDataModel: any) {
    const reducedModel = {
        isPreciseDestination: currentDataModel.isPreciseDestination,
        isInternational: currentDataModel.isInternational,
        isBringingPets: currentDataModel.isBringingPets,
        isBringingChildren: currentDataModel.isBringingChildren,
        seatsNeeded: currentDataModel.seatsNeeded,
        travelTiming: {
            departureDateExact: currentDataModel.travelTiming?.departureDateExact ?? null,
            departureDateRange: {
                start: currentDataModel.travelTiming?.departureDateRange?.start ?? null,
                end: currentDataModel.travelTiming?.departureDateRange?.end ?? null,
            },
            departureTimeExact: currentDataModel.travelTiming?.departureTimeExact ?? null,
            departureTimeRange: {
                start: currentDataModel.travelTiming?.departureTimeRange?.start ?? null,
                end: currentDataModel.travelTiming?.departureTimeRange?.end ?? null,
            }
        },
        arrivalData: {
            preciseDestination: {
                locationName: currentDataModel.arrivalData?.preciseDestination?.locationName ?? null,
                state: currentDataModel.arrivalData?.preciseDestination?.state ?? null,
                country: currentDataModel.arrivalData?.preciseDestination?.country ?? null,
            },
            generalDestination: {
                destinationType: currentDataModel.arrivalData?.generalDestination?.destinationType ?? null,
                country: currentDataModel.arrivalData?.generalDestination?.country ?? null,
                state: currentDataModel.arrivalData?.generalDestination?.state ?? null,
            },
        },
        departureData: {
            locationName: currentDataModel.departureData?.locationName ?? null,
        },
        userData: {
            preferences: {
                isQuietFlightOnly: currentDataModel.userData?.preferences?.isQuietFlightOnly ?? false,
                isPetOk: currentDataModel.userData?.preferences?.isPetOk ?? true,
                isChildrenOk: currentDataModel.userData?.preferences?.isChildrenOk ?? true,
            },
            membership: {
                aircraft: currentDataModel.userData?.membership?.aircraft ?? null,
                aircraftMaxSeats: currentDataModel.userData?.membership?.aircraftMaxSeats ?? null,
            }
        },
        reservationData: {
            isExistingReservation: currentDataModel.reservationData?.isExistingReservation ?? false,
            seatsOffered: currentDataModel.reservationData?.seatsOffered ?? null,
        }
    } as const;
    return reducedModel;
}

export function handleReduceFlightOptions(flightOptions: any) {
    const reducedOptions = flightOptions.map(o => ({
        id: o.id,
        // Locations
        departureLocationName: o.departureLocationName,
        arrivalLocationName: o.arrivalLocationName,
        arrivalGeneralType: o.arrivalGeneralType,
        arrivalGeneralCountry: o.arrivalGeneralCountry,
        arrivalGeneralState: o.arrivalGeneralState,
        arrivalPreciseType: o.arrivalPreciseType,
        arrivalPreciseCountry: o.arrivalPreciseCountry,
        arrivalPreciseState: o.arrivalPreciseState,
        // Dates + Times
        departureDateExact: o.departureDateExact,
        departureDateRangeStart: o.departureDateRangeStart,
        departureDateRangeEnd: o.departureDateRangeEnd,
        departureTimeExact: o.departureTimeExact,
        departureTimeRangeStart: o.departureTimeRangeStart,
        departureTimeRangeEnd: o.departureTimeRangeEnd,
        timeZone: o.timeZone,
        // Preferences
        isBringingPets: o.isBringingPets,
        isBringingChildren: o.isBringingChildren,
        isQuietFlightOnly: o.isQuietFlightOnly,
        isPetOk: o.isPetOk,
        isChildrenOk: o.isChildrenOk,
        // Seats
        seatsNeeded: o.seatsNeeded,
        seatsOffered: o.seatsOffered,
        // Aircraft
        aircraft: o.aircraft,
        aircraftMaxSeats: o.aircraftMaxSeats,
        // Mood / type
        expectedMood: o.expectedMood,
        isExistingReservation: o.isExistingReservation,
        failures: o.failures,
    }));
    return reducedOptions;
}

export function getFriendlyRoute(from: string, to: {
    generalType?: any;
    generalState?: any;   
    generalCountry?: any; 
    locationName?: any;   
  }): string {
    const formatFrom = (location: string): string => {
      if (!location) return 'Unknown';
      const parts = location.split(',').map(p => p.trim());
      return parts[0]; // city only (e.g., "Dallas")
    };
  
    const formatTo = (): string => {
      const { generalType, generalState, generalCountry, locationName } = to;
  
      if (locationName) {
        const parts = locationName.split(',').map(p => p.trim());
        return parts[2] === 'US' ? parts[0] : parts.filter(Boolean).join(', ');
      }
  
      const typePart = generalType ? `any ${generalType.toLowerCase()}` : 'anywhere';
      const regionParts = [];
  
      if (generalState) regionParts.push(`in ${generalState}`);
      if (!generalState && generalCountry) regionParts.push(`in ${generalCountry}`);
  
      return `${typePart} ${regionParts.join(' ')}`.trim();
    };
  
    return `${formatFrom(from)} to ${formatTo()}`;
}

export function getDecorativeDates({
    departureDateExact,
    departureDateRangeStart,
    departureDateRangeEnd,
  }: {
    departureDateExact?: string | null;
    departureDateRangeStart?: string | null;
    departureDateRangeEnd?: string | null;
  }): { month: string; days: string } | null {
    const addOrdinal = (day: number): string => {
      const suffix = ['th', 'st', 'nd', 'rd'];
      const v = day % 100;
      return day + (suffix[(v - 20) % 10] || suffix[v] || 'th');
    };
  
    const parseDate = (input: string) => new Date(input + 'T00:00:00');
  
    if (departureDateExact) {
      const date = parseDate(departureDateExact);
      const month = date.toLocaleString('en-US', { month: 'short' });
      const day = addOrdinal(date.getDate());
      return { month, days: day };
    }
  
    if (departureDateRangeStart && departureDateRangeEnd) {
      const start = parseDate(departureDateRangeStart);
      const end = parseDate(departureDateRangeEnd);
  
      const startMonth = start.toLocaleString('en-US', { month: 'short' });
      const endMonth = end.toLocaleString('en-US', { month: 'short' });
  
      const startDay = addOrdinal(start.getDate());
      const endDay = addOrdinal(end.getDate());
  
      if (start.getMonth() !== end.getMonth()) {
        return {
          month: `${startMonth}-${endMonth}`,
          days: `${startDay}-${endDay}`
        };
      }
  
      return {
        month: startMonth,
        days: `${startDay}-${endDay}`
      };
    }
  
    return null;
}
  
  